import { useCallback } from "react";

import { useDispatch } from "react-redux";

import type { MenuItemOption } from "app/pages/sales-intelligence/common-components/options-menu/types";
import crmEnrichmentTrackingService from "app/pages/sales-intelligence/modules/crm-enrichment/services/tracking/trackingService";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";

import CrmEnrichmentToggle from "../components/CrmEnrichmentToggle/CrmEnrichmentToggle";
import { useCrmEnrichmentContext } from "../contexts/CrmEnrichmentContext";
import { withCrmEnrichmentJobTracking } from "../hocs/withCrmEnrichmentJobTracking";
import useDownloadRunResultFile from "../hooks/useDownloadRunResultFile";
import { setRemoveModalOpen, setRenameModalOpen } from "../store/action-creators";
import type { CrmEnrichmentJob } from "../types/types";

type Props = {
    job: CrmEnrichmentJob;
    onRenameClick?: (job: CrmEnrichmentJob) => void;
    onDeleteClick?: (job: CrmEnrichmentJob) => void;
    onToggle: (jobId: string, value: boolean) => void;
    onToggleMenu?: (isOpen: boolean, jobName: string) => void;
    onSelectOption?: (option: MenuItemOption) => void;
};

const CrmEnrichmentToggleContainer = ({
    job,
    onToggle,
    onToggleMenu,
    onSelectOption,
    onRenameClick,
    onDeleteClick,
}: Props) => {
    const dispatch = useDispatch();

    const { crmProvider } = useCrmEnrichmentContext();
    const { mutate: downloadRunResultFile } = useDownloadRunResultFile(crmProvider);

    const handleEdit = (jobId: string) => {
        void siNavigator.goToCrmEnrichmentJobEditPage({ crm: crmProvider, jobId });
    };

    const handleLogs = (jobId: string) => {
        crmEnrichmentTrackingService.trackLogsButtonClick(crmProvider);

        void siNavigator.goToCrmEnrichmentLogsPage({ crm: crmProvider, jobId });
    };

    const handleRename = () => {
        dispatch(setRenameModalOpen(true));
        onRenameClick?.(job);
    };

    const handleDelete = () => {
        dispatch(setRemoveModalOpen(true));
        onDeleteClick?.(job);
    };

    const handleViewLogFile = useCallback(
        (jobId: string, runId: string, isError: boolean) => {
            crmEnrichmentTrackingService.trackDownloadLogs(isError ? "red banner" : "grey banner");

            downloadRunResultFile({ jobId, runId });
        },
        [downloadRunResultFile],
    );

    return (
        <CrmEnrichmentToggle
            job={job}
            crmProvider={crmProvider}
            onToggle={onToggle}
            onEdit={handleEdit}
            onLogs={handleLogs}
            onRename={handleRename}
            onDelete={handleDelete}
            onViewLogFile={handleViewLogFile}
            onToggleMenu={onToggleMenu}
            onSelectOption={onSelectOption}
        />
    );
};

export default withCrmEnrichmentJobTracking(CrmEnrichmentToggleContainer);
